"use client";

import React, { createContext, useContext } from "react";
import type { SendAgentMessageOptions } from "@/hooks/create-page/actions/useAgentActions";
import type { DesignSpec } from "@/types/designSpec";
import type { DesignRenderExecutionOptions } from "@/lib/designer/applyDesignChanges";

/**
 * The hook-bound actions the chat subtree needs. Only actions that live in
 * useCreatePage's action hooks (session, mutations, router) belong here —
 * plain store actions (updateFormData, setStep, addMessage, setCurrentView…)
 * are read directly from useCreatePageStore, and state comes from store
 * selectors, so this context stays deliberately small.
 */
export interface ChatActions {
  sendAgentMessage: (
    message: string,
    imageUrl?: string,
    imageUrls?: string[],
    options?: SendAgentMessageOptions
  ) => Promise<void>;
  generateImages: (
    targetStep?: number,
    isChatMode?: boolean,
    designSpecOverride?: DesignSpec,
    options?: DesignRenderExecutionOptions
  ) => Promise<void>;
  modifyImage: (
    promptOverride?: string,
    designSpecOverride?: DesignSpec,
    options?: DesignRenderExecutionOptions
  ) => Promise<void>;
  handleCreateTicket: (
    designSpec?: DesignSpec,
    options?: { quoteScope?: "front_only" }
  ) => Promise<boolean | void> | boolean | void;
  handleStartOver: () => void;
}

const ChatActionsContext = createContext<ChatActions | null>(null);

export function ChatActionsProvider({
  actions,
  children,
}: {
  actions: ChatActions;
  children: React.ReactNode;
}) {
  return (
    <ChatActionsContext.Provider value={actions}>
      {children}
    </ChatActionsContext.Provider>
  );
}

export function useChatActions(): ChatActions {
  const context = useContext(ChatActionsContext);
  if (!context) {
    throw new Error(
      "useChatActions must be used within a ChatActionsProvider (rendered by CreateDressPageContent)"
    );
  }
  return context;
}
